import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Mail, Lock, Phone, MapPin, Calendar, Building2, ArrowRight, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { DateInputFR } from '../components/DateInputFR';
import { calculerAge } from '../utils/age';

const InscriptionCandidat = () => {
  const [searchParams] = useSearchParams();
  const emailFromUrl = searchParams.get('email') || '';
  const tokenFromUrl = searchParams.get('token') || '';

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    email: emailFromUrl,
    password: '',
    confirmPassword: '',
    nom: '',
    prenom: '',
    telephone: '',
    date_naissance: '',
    lieu_naissance: '',
    adresse: '',
    code_postal: '',
    ville: '',
    centre: 'atlantique'
  });

  const [errors, setErrors] = useState({});

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.email) newErrors.email = "L'email est requis";
    if (!formData.password) newErrors.password = "Le mot de passe est requis";
    if (formData.password.length < 6) newErrors.password = "Le mot de passe doit faire au moins 6 caractères";
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = "Les mots de passe ne correspondent pas";
    if (!formData.nom) newErrors.nom = "Le nom est requis";
    if (!formData.prenom) newErrors.prenom = "Le prénom est requis";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const adresseErrors = {};
    if (!formData.adresse.trim()) adresseErrors.adresse = "L'adresse est requise";
    if (!formData.code_postal.trim()) adresseErrors.code_postal = 'Le code postal est requis';
    if (!formData.ville.trim()) adresseErrors.ville = 'La ville est requise';
    setErrors(adresseErrors);
    if (Object.keys(adresseErrors).length > 0) {
      toast.error("L'adresse complète est obligatoire (elle figure sur votre convention de formation)");
      return;
    }
    setLoading(true);

    try {
      const response = await axios.post('/candidats/inscription', {
        email: formData.email,
        password: formData.password,
        nom: formData.nom,
        prenom: formData.prenom,
        telephone: formData.telephone || null,
        date_naissance: formData.date_naissance || null,
        lieu_naissance: formData.lieu_naissance || null,
        adresse: formData.adresse.trim(),
        code_postal: formData.code_postal.trim(),
        ville: formData.ville.trim(),
        centre: formData.centre
      });

      setSuccess(true);
      toast.success('Inscription réussie !');
    } catch (error) {
      const message = error.response?.data?.detail || "Erreur lors de l'inscription";
      toast.error(message);
      setErrors({ submit: message });
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-[#0F172A] mb-2">Inscription réussie !</h2>
          <p className="text-slate-600 mb-6">
            Votre compte candidat a été créé. Vous pouvez maintenant vous connecter et accéder à votre espace.
          </p>
          <Link to="/login">
            <Button className="w-full bg-[#8DC63F] hover:bg-[#7db336]">
              Se connecter
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src="/logo-fisl.png" alt="FISL" className="h-12 w-12 rounded object-contain bg-slate-100 p-1" />
            <div className="text-left">
              <h1 className="text-xl font-bold text-[#0F172A]">FISL</h1>
              <p className="text-xs text-slate-500">Formation Motonautisme</p>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-[#0F172A]">Inscription Candidat</h2>
          <p className="text-slate-500 mt-2">Tests de sélection BPJEPS Motonautisme</p>
          <p className="mt-3 rounded-lg bg-[#8DC63F]/10 px-3 py-2 text-sm text-[#4d7a16]" data-testid="intro-inscription-candidat">
            Pour candidater à une formation FISL, vous devez commencer par créer un compte sécurisé.
          </p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-[#8DC63F]' : 'text-slate-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 1 ? 'bg-[#8DC63F] text-white' : 'bg-slate-200'}`}>
              1
            </div>
            <span className="text-sm font-medium hidden sm:inline">Compte</span>
          </div>
          <div className={`w-12 h-0.5 ${step >= 2 ? 'bg-[#8DC63F]' : 'bg-slate-200'}`} />
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-[#8DC63F]' : 'text-slate-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${step >= 2 ? 'bg-[#8DC63F] text-white' : 'bg-slate-200'}`}>
              2
            </div>
            <span className="text-sm font-medium hidden sm:inline">Informations</span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="prenom">Prénom *</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="prenom"
                      value={formData.prenom}
                      onChange={(e) => setFormData({...formData, prenom: e.target.value})}
                      className={`pl-10 ${errors.prenom ? 'border-red-500' : ''}`}
                      placeholder="Jean"
                    />
                  </div>
                  {errors.prenom && <p className="text-red-500 text-xs mt-1">{errors.prenom}</p>}
                </div>
                <div>
                  <Label htmlFor="nom">Nom *</Label>
                  <div className="relative mt-1">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="nom"
                      value={formData.nom}
                      onChange={(e) => setFormData({...formData, nom: e.target.value})}
                      className={`pl-10 ${errors.nom ? 'border-red-500' : ''}`}
                      placeholder="Dupont"
                    />
                  </div>
                  {errors.nom && <p className="text-red-500 text-xs mt-1">{errors.nom}</p>}
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email *</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                    placeholder="jean.dupont@email.com"
                  />
                </div>
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              <div>
                <Label htmlFor="password">Mot de passe *</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className={`pl-10 pr-12 ${errors.password ? 'border-red-500' : ''}`}
                    placeholder="••••••••"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors" data-testid="toggle-password">
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirmer le mot de passe *</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                    className={`pl-10 pr-12 ${errors.confirmPassword ? 'border-red-500' : ''}`}
                    placeholder="••••••••"
                  />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors" data-testid="toggle-confirm-password">
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {errors.confirmPassword && <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>}
              </div>

              <Button
                type="button"
                onClick={handleNext}
                className="w-full bg-[#8DC63F] hover:bg-[#7db336] mt-6"
              >
                Continuer
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="telephone">Téléphone</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="telephone"
                    value={formData.telephone}
                    onChange={(e) => setFormData({...formData, telephone: e.target.value})}
                    className="pl-10"
                    placeholder="06 12 34 56 78"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="date_naissance">
                  Date de naissance
                  {calculerAge(formData.date_naissance) !== null && (
                    <span className="ml-2 text-[#8DC63F] font-semibold" data-testid="inscription-age">({calculerAge(formData.date_naissance)} ans)</span>
                  )}
                </Label>
                <div className="relative mt-1">
                  <Calendar className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                  <DateInputFR
                    id="date_naissance"
                    value={formData.date_naissance}
                    onChange={(iso) => setFormData(prev => ({ ...prev, date_naissance: iso }))}
                    className="pl-10"
                    data-testid="inscription-date-naissance-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="lieu_naissance">Lieu de naissance</Label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="lieu_naissance"
                    value={formData.lieu_naissance}
                    onChange={(e) => setFormData({...formData, lieu_naissance: e.target.value})}
                    className="pl-10"
                    placeholder="Paris"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="adresse">Adresse *</Label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="adresse"
                    value={formData.adresse}
                    onChange={(e) => setFormData({...formData, adresse: e.target.value})}
                    className={`pl-10 ${errors.adresse ? 'border-red-500' : ''}`}
                    placeholder="12 rue de la Mer"
                    data-testid="inscription-adresse-input"
                  />
                </div>
                {errors.adresse && <p className="text-red-500 text-xs mt-1" data-testid="inscription-adresse-error">{errors.adresse}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="code_postal">Code postal *</Label>
                  <Input
                    id="code_postal"
                    value={formData.code_postal}
                    onChange={(e) => setFormData({...formData, code_postal: e.target.value})}
                    placeholder="17000"
                    className={errors.code_postal ? 'border-red-500' : ''}
                    data-testid="inscription-code-postal-input"
                  />
                  {errors.code_postal && <p className="text-red-500 text-xs mt-1" data-testid="inscription-code-postal-error">{errors.code_postal}</p>}
                </div>
                <div>
                  <Label htmlFor="ville">Ville *</Label>
                  <Input
                    id="ville"
                    value={formData.ville}
                    onChange={(e) => setFormData({...formData, ville: e.target.value})}
                    placeholder="La Rochelle"
                    className={errors.ville ? 'border-red-500' : ''}
                    data-testid="inscription-ville-input"
                  />
                  {errors.ville && <p className="text-red-500 text-xs mt-1" data-testid="inscription-ville-error">{errors.ville}</p>}
                </div>
              </div>

              <div>
                <Label htmlFor="centre">Centre de formation *</Label>
                <div className="relative mt-1">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <select
                    id="centre"
                    value={formData.centre}
                    onChange={(e) => setFormData({...formData, centre: e.target.value})}
                    className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F] focus:border-transparent"
                  >
                    <option value="atlantique">FISL Atlantique (St-Georges-de-Didonne)</option>
                    <option value="occitanie">FISL Occitanie</option>
                  </select>
                </div>
              </div>

              {errors.submit && (
                <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                  {errors.submit}
                </div>
              )}

              <div className="flex gap-3 mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1"
                >
                  Retour
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-[#8DC63F] hover:bg-[#7db336]"
                  data-testid="inscription-submit-btn"
                >
                  {loading ? 'Inscription...' : "S'inscrire"}
                </Button>
              </div>
            </div>
          )}
        </form>

        <p className="text-center text-sm text-slate-500 mt-6">
          Déjà inscrit ?{' '}
          <Link to="/login" className="text-[#8DC63F] hover:underline font-medium">
            Se connecter
          </Link>
        </p>
      </div>
    </div>
  );
};

export default InscriptionCandidat;
